import type { CSSProperties } from 'react';
import { memo } from 'react';
import type { ThemeSpaces } from '@frameio/vapor';
import { useActiveWindowBreakpoint } from '@frameio/vapor';
import styles from './ListItemCard.module.css';

type ListItemCardProps = {
  contextMenu?: React.ReactNode;
  detailComponent: React.ReactNode;
  disableHoverStyles?: boolean;
  imageComponent: React.ReactNode;
  isActive: boolean;
  isCompact?: boolean;
  matchesComponent?: React.ReactNode;
  onContextMenu?: (event: React.MouseEvent) => void;
  onDoubleClick?: () => void;
  paddingInlineEnd?: ThemeSpaces;
  titleComponent: React.ReactNode;
};

export const LIST_ITEM_CARD_PADDING = 8;

export const ListItemCard = memo(
  ({
    contextMenu,
    detailComponent,
    disableHoverStyles = false,
    imageComponent,
    isActive,
    isCompact = false,
    matchesComponent,
    onContextMenu,
    onDoubleClick,
    titleComponent,
  }: ListItemCardProps) => {
    const breakpoint = useActiveWindowBreakpoint();
    const isMobile = isCompact || breakpoint === 'base';
    const detailsGap = isMobile ? 2 : 4;

    return (
      <div
        className={styles.card}
        data-mobile={isMobile}
        data-testid="quick-search-v2-list-item-card"
        onContextMenu={onContextMenu}
        onDoubleClick={onDoubleClick}
      >
        <div
          className={styles.interactiveCard}
          data-active={isActive}
          data-disable-hover-styles={disableHoverStyles}
          data-mobile={isMobile}
        >
          <div className={styles.cardContent} data-mobile={isMobile}>
            <div className={styles.mainContent}>
              {imageComponent}
              <div className={styles.infoContainer}>
                <div
                  className={styles.detailsContainer}
                  style={
                    { '--details-gap': `${detailsGap}px` } as CSSProperties
                  }
                >
                  {titleComponent}
                  {detailComponent}
                </div>
                {(!isMobile || matchesComponent) && (
                  <div className={styles.matches} data-mobile={isMobile}>
                    {matchesComponent || null}
                  </div>
                )}
              </div>
            </div>
            {contextMenu && (
              <div className={styles.contextMenuSlot}>{contextMenu}</div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

ListItemCard.displayName = 'ListItemCard';
